// RdPopover.tsx — kleines, Redesign-natives Popover für Tabellen-Header
// (Spaltenfilter, Spalten-Menü). Rendert den Inhalt per Portal an `body` mit
// `position:fixed`, damit er NICHT vom `overflow:auto`-Scroll-Container der
// Tabelle abgeschnitten wird (Dropdown-Clipping-Lektion). Schließt bei
// Außenklick, Esc, Scroll/Resize-Reposition.

import * as React from "react";
import { createPortal } from "../../windowManager/browserFenster/fensterPortal";
import { alleFenster, aktivesDokument, aktivesFenster } from "../../windowManager/browserFenster/fensterRegister";

type TriggerProps = { open: boolean; toggle: () => void; ref: React.Ref<HTMLButtonElement> };

/** Abstand des Popovers zum Trigger und zum Viewport-Rand. */
const GAP = 4;
const RAND = 8;

export function RdPopover({
  trigger,
  children,
  align = "start",
  width = 240,
  dialogLabel,
  anPlatzAnpassen = false,
}: {
  trigger: (p: TriggerProps) => React.ReactNode;
  children: (close: () => void) => React.ReactNode;
  align?: "start" | "end";
  width?: number;
  /**
   * Macht das Popover zu einem benannten Dialog: `role="dialog"` mit diesem
   * Namen, Fokus wandert beim Öffnen hinein und beim Schließen zum Auslöser
   * zurück.
   *
   * Bewusst OPT-IN und nicht der Standard: Popover, die sich beim Tippen im
   * Auslöser öffnen (CalendarSearchBox), verlören sonst mitten im Wort den
   * Fokus. Ohne Namen bliebe außerdem nur die nackte Rollenansage „Dialog".
   */
  dialogLabel?: string;
  /**
   * Passt das Popover an den verfügbaren Platz an (PO 01.11.2026: „das Popup
   * wird abgeschnitten, wenn die Vorgangsseite klein ist"): nie breiter als
   * das Fenster, und ist es höher als der Platz an der gewählten Seite, kappt
   * es dort und scrollt innen. Gemessen wird die NATÜRLICHE Höhe
   * (`scrollHeight`) — mit der gekappten kippte es sonst zwischen oben und
   * unten hin und her (Lektion `hoverPlatzierung.ts`).
   *
   * Opt-in, weil `overflow:auto` absolut positionierte Kinder anderer
   * Popover (Untermenüs) beschneiden würde.
   */
  anPlatzAnpassen?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState<{ top: number; left: number; width: number; maxHeight?: number } | null>(null);
  const btnRef = React.useRef<HTMLButtonElement>(null);
  const popRef = React.useRef<HTMLDivElement>(null);

  const place = React.useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const fenster = aktivesFenster();
    const breite = anPlatzAnpassen ? Math.max(0, Math.min(width, fenster.innerWidth - 2 * RAND)) : width;
    let left = align === "end" ? r.right - breite : r.left;
    left = Math.max(RAND, Math.min(left, fenster.innerWidth - breite - RAND));

    // Nach OBEN klappen, wenn unten nicht genug Platz ist. Ohne diesen Flip
    // begann ein Popover in der letzten Tabellenzeile knapp über der
    // Viewport-Unterkante — der ganze Inhalt lag außerhalb und wanderte beim
    // Scrollen mit, kam also nie in Sicht. Die Höhe ist erst nach dem Mount
    // messbar; bis dahin (und wenn oben genauso wenig Platz ist) bleibt es beim
    // Platz unterhalb.
    const hoehe = (anPlatzAnpassen ? popRef.current?.scrollHeight : popRef.current?.offsetHeight) ?? 0;
    const platzUnten = fenster.innerHeight - r.bottom - RAND;
    const platzOben = r.top - RAND;
    const nachOben = hoehe > 0 && platzUnten < hoehe && platzOben > platzUnten;
    if (anPlatzAnpassen) {
      // Was an der gewählten Seite ins Fenster passt; mindestens ein paar
      // Zeilen, sonst wäre der Inhalt auch mit Scrollen nicht bedienbar.
      const maxHeight = Math.min(
        Math.max(120, (nachOben ? platzOben : platzUnten) - GAP),
        fenster.innerHeight - 2 * RAND,
      );
      const sichtbar = hoehe > 0 ? Math.min(hoehe, maxHeight) : 0;
      const wunsch = nachOben ? r.top - GAP - sichtbar : r.bottom + GAP;
      const top = Math.max(RAND, Math.min(wunsch, fenster.innerHeight - RAND - sichtbar));
      setPos({ top, left, width: breite, maxHeight });
      return;
    }
    const top = nachOben
      ? Math.max(RAND, r.top - GAP - hoehe)
      : Math.min(r.bottom + GAP, fenster.innerHeight - RAND);
    setPos({ top, left, width: breite });
  }, [align, width, anPlatzAnpassen]);

  const toggle = React.useCallback(() => {
    setOpen((o) => {
      if (!o) place();
      return !o;
    });
  }, [place]);
  const close = React.useCallback(() => setOpen(false), []);

  // Zweiter Durchgang direkt nach dem Mount: jetzt steht die Höhe fest, erst
  // damit kann `place` überhaupt nach oben klappen.
  React.useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  // Wächst oder schrumpft der Inhalt (Trefferliste beim Tippen, Fehlermeldung),
  // neu platzieren — sonst ragte ein nach oben geklapptes Popover über seinen
  // Auslöser oder unten aus dem Fenster. Nur mit `anPlatzAnpassen`; jsdom
  // kennt keinen ResizeObserver.
  // Das Portal hängt erst, wenn `pos` steht — vorher gibt es nichts zu beobachten.
  const gesetzt = pos != null;
  React.useEffect(() => {
    if (!open || !anPlatzAnpassen || typeof ResizeObserver === "undefined") return;
    const el = popRef.current;
    if (!el) return;
    let letzte = -1;
    const ro = new ResizeObserver(() => {
      const h = el.scrollHeight;
      if (h === letzte) return;
      letzte = h;
      place();
    });
    // Beobachtet wird der Inhalt — die Hülle selbst ist ja gekappt.
    Array.from(el.children).forEach((c) => ro.observe(c));
    return () => ro.disconnect();
  }, [open, anPlatzAnpassen, place, gesetzt]);

  // Fokus IN das Popover holen, sofern der Inhalt ihn nicht selbst genommen hat.
  // Ohne das erreichen Tastatur- und Screenreader-Nutzer den Portal-Inhalt gar
  // nicht: er hängt als letztes Kind an `body`, weder Tab noch virtueller Cursor
  // springen dorthin.
  //
  // Das Flag ist nötig, weil `pos` sich bei jedem Scroll ändert — ohne es würde
  // der Fokus beim Scrollen immer wieder neu gesetzt.
  const fokusGesetzt = React.useRef(false);
  React.useEffect(() => {
    if (!open || !dialogLabel || fokusGesetzt.current) return;
    const el = popRef.current;
    if (!el) return;
    fokusGesetzt.current = true;
    if (!el.contains(aktivesDokument().activeElement)) el.focus();
  }, [open, pos, dialogLabel]);

  // Beim Schließen zurück auf den Auslöser — aber nur, wenn der Fokus mit dem
  // Portal verschwunden ist (dann liegt er auf `body`). Hat der Nutzer daneben
  // auf ein anderes Bedienelement geklickt, bleibt er dort.
  React.useEffect(() => {
    if (!open || !dialogLabel) return;
    return () => {
      fokusGesetzt.current = false;
      if (aktivesDokument().activeElement === aktivesDokument().body) btnRef.current?.focus();
    };
  }, [open, dialogLabel]);

  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      // Eine Auswahlliste, die ein Feld IM Popover als Portal am `body`
      // aufspannt (`@`/`#` im Kommentar, `AuswahlAnker`), gehört fachlich
      // dazu — ein Klick hinein darf das Popover nicht schließen.
      if ((t as Element | null)?.closest?.("[data-popover-teil]")) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    alleFenster.addEventListener("pointerdown", onDown);
    alleFenster.addEventListener("keydown", onKey);
    alleFenster.addEventListener("resize", place);
    alleFenster.addEventListener("scroll", place, true); // capture: any ancestor scroller
    return () => {
      alleFenster.removeEventListener("pointerdown", onDown);
      alleFenster.removeEventListener("keydown", onKey);
      alleFenster.removeEventListener("resize", place);
      alleFenster.removeEventListener("scroll", place, true);
    };
  }, [open, close, place]);

  return (
    <>
      {trigger({ open, toggle, ref: btnRef })}
      {open && pos
        ? createPortal(
            <div
              ref={popRef}
              className="rd-popover"
              role={dialogLabel ? "dialog" : undefined}
              aria-label={dialogLabel}
              tabIndex={dialogLabel ? -1 : undefined}
              style={{
                position: "fixed",
                top: pos.top,
                left: pos.left,
                width: pos.width,
                zIndex: 2000,
                ...(pos.maxHeight != null
                  ? { maxHeight: pos.maxHeight, overflowY: "auto" as const, overscrollBehavior: "contain" as const }
                  : null),
              }}
            >
              {children(close)}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
